/**
 * Reading a supplier's price list file in the browser (EOS-59). The server
 * never parses an uploaded file: the `xlsx` version in use has known
 * weaknesses when reading crafted files, so the file is read here, in the
 * user's own tab, and only the cells of its sheets go to the server.
 */
import type {
  FixedPriceImportColumnMapping,
  FixedPriceImportField,
  FixedPriceImportRowStatus,
  FixedPriceImportSheet,
  PriceListColumnName,
} from '@/api/preis-tool/preis-tool.types';

/** Same limits the backend checks (`fixed-prices/domain/price-list-file.ts`). */
export const MAX_PRICE_LIST_FILE_BYTES = 5 * 1024 * 1024;
export const MAX_PRICE_LIST_ROWS = 5000;
export const MAX_PRICE_LIST_TOTAL_ROWS = 10000;
export const MAX_PRICE_LIST_SENT_ROWS = 10000;
export const MAX_PRICE_LIST_COLUMNS = 40;
export const MAX_PRICE_LIST_SHEETS = 20;
/** The header row is one of this many non-empty rows on top of a sheet. */
export const HEADER_SEARCH_ROWS = 20;

const SUPPORTED_EXTENSIONS = ['xlsx', 'xls', 'csv'];

export type PriceListReadProblem =
  | 'UNSUPPORTED_FORMAT'
  | 'FILE_TOO_LARGE'
  | 'UNREADABLE'
  | 'EMPTY'
  | 'TOO_MANY_ROWS'
  | 'TOO_MANY_SHEETS';

export class PriceListReadError extends Error {
  constructor(readonly problem: PriceListReadProblem) {
    super(problem);
  }
}

/** Why a single sheet is shown but not sent. */
export type PriceListSheetProblem = 'EMPTY' | 'TOO_MANY_ROWS' | 'TOO_MANY_COLUMNS';

export interface PriceListSheetCells {
  name: string;
  /** One array of cells per row, starting at row 1, empty rows included. */
  rows: (string | number)[][];
  /** Set for a sheet that is not sent to the server. */
  problem: PriceListSheetProblem | null;
}

export interface PriceListWorkbook {
  fileName: string;
  /** Every sheet of the file in its order; a CSV file is one sheet. */
  sheets: PriceListSheetCells[];
}

/** Reads every sheet of an xlsx / xls / csv file. */
export async function readPriceListFile(file: File): Promise<PriceListWorkbook> {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!SUPPORTED_EXTENSIONS.includes(extension)) throw new PriceListReadError('UNSUPPORTED_FORMAT');
  if (file.size > MAX_PRICE_LIST_FILE_BYTES) throw new PriceListReadError('FILE_TOO_LARGE');

  let sheets: PriceListSheetCells[];
  try {
    const XLSX = await import('xlsx');
    const buffer = await file.arrayBuffer();
    // `raw` keeps CSV cells as text, so an EAN keeps its leading zeros and a
    // German decimal comma is not misread. `sheetRows` bounds the work.
    const options = { raw: true, sheetRows: MAX_PRICE_LIST_SENT_ROWS };
    const workbook =
      extension === 'csv'
        ? XLSX.read(decodeText(buffer), { ...options, type: 'string' })
        : XLSX.read(buffer, { ...options, type: 'array' });
    if (workbook.SheetNames.length > MAX_PRICE_LIST_SHEETS) throw new PriceListReadError('TOO_MANY_SHEETS');
    sheets = workbook.SheetNames.map((name) => readSheet(XLSX, workbook.Sheets[name], name));
  } catch (error) {
    if (error instanceof PriceListReadError) throw error;
    throw new PriceListReadError('UNREADABLE');
  }

  const sent = sheets.filter((s) => !s.problem);
  if (!sent.some((s) => filledRowCount(s.rows) >= 2)) throw new PriceListReadError('EMPTY');
  if (sent.reduce((sum, s) => sum + filledRowCount(s.rows), 0) > MAX_PRICE_LIST_TOTAL_ROWS) {
    throw new PriceListReadError('TOO_MANY_ROWS');
  }
  return { fileName: file.name, sheets };
}

/** UTF-8 when the bytes are valid UTF-8, else Windows-1252 (Excel's "CSV" on Windows). */
function decodeText(buffer: ArrayBuffer): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder('windows-1252').decode(buffer);
  }
}

type Xlsx = typeof import('xlsx');
type Sheet = import('xlsx').WorkSheet;

function readSheet(XLSX: Xlsx, sheet: Sheet | undefined, name: string): PriceListSheetCells {
  const ref = sheet?.['!ref'];
  if (!sheet || !ref) return { name, rows: [], problem: 'EMPTY' };
  // The whole sheet, also beyond the rows read (`sheetRows`).
  const full = XLSX.utils.decode_range(sheet['!fullref'] ?? ref);
  if (full.e.c + 1 > MAX_PRICE_LIST_COLUMNS) return { name, rows: [], problem: 'TOO_MANY_COLUMNS' };
  if (full.e.r + 1 > MAX_PRICE_LIST_SENT_ROWS) return { name, rows: [], problem: 'TOO_MANY_ROWS' };

  // From A1 on, so a row's index is its row number − 1 and a cell's index its column.
  const table = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
    header: 1,
    raw: true,
    defval: '',
    blankrows: true,
    range: { s: { r: 0, c: 0 }, e: XLSX.utils.decode_range(ref).e },
  });
  const rows = table.map((row) => trimEnd(row.map(cellValue)));
  while (rows.length > 0 && rows[rows.length - 1].length === 0) rows.pop();
  const filled = filledRowCount(rows);
  if (filled === 0) return { name, rows: [], problem: 'EMPTY' };
  if (filled > MAX_PRICE_LIST_ROWS + HEADER_SEARCH_ROWS) return { name, rows: [], problem: 'TOO_MANY_ROWS' };
  return { name, rows, problem: null };
}

/** Numbers stay numbers, everything else becomes trimmed text. */
function cellValue(value: unknown): string | number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (value == null) return '';
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).trim();
}

/** Without the empty cells at the end of the row — they only cost bytes. */
function trimEnd(row: (string | number)[]): (string | number)[] {
  let end = row.length;
  while (end > 0 && row[end - 1] === '') end--;
  return row.slice(0, end);
}

const isFilled = (row: (string | number)[] | undefined) => !!row && row.some((cell) => cell !== '');

const filledRowCount = (rows: (string | number)[][]) => rows.filter(isFilled).length;

/** The rows the header row can be chosen from: the first non-empty ones, with their text. */
export function headerRowCandidates(rows: (string | number)[][]): { rowNumber: number; text: string }[] {
  return rows
    .map((row, i) => ({ row, rowNumber: i + 1 }))
    .filter(({ row }) => isFilled(row))
    .slice(0, HEADER_SEARCH_ROWS)
    .map(({ row, rowNumber }) => ({
      rowNumber,
      text: row.filter((cell) => cell !== '').join(' · '),
    }));
}

export const FIELD_LABELS: Record<FixedPriceImportField, string> = {
  itemNumber: 'Artikelnummer',
  ean: 'EAN',
  netPrice: 'EK netto',
  uvpNet: 'UVP netto',
  uvpGross: 'UVP brutto',
  quantity: 'Menge',
  description: 'Bezeichnung',
};

export const FIELDS = Object.keys(FIELD_LABELS) as FixedPriceImportField[];

export const IGNORE_LABEL = 'nicht verwenden';

export const targetLabel = (field: PriceListColumnName['field']) =>
  field === 'ignore' ? IGNORE_LABEL : FIELD_LABELS[field];

/** Same as the backend's `normaliseColumnName`: case, punctuation and spacing do not count. */
export function normaliseColumnName(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9äöüß]+/g, ' ')
    .trim();
}

/** The field a column is used as in a mapping, `null` when none. */
export function fieldOfColumn(mapping: FixedPriceImportColumnMapping, index: number): FixedPriceImportField | null {
  return FIELDS.find((field) => mapping[field] === index) ?? null;
}

/**
 * The column names an import would add — the backend's `learnedColumnNames`,
 * so the dialog can show them before the import: on every imported sheet,
 * each column assigned differently than suggested teaches its header for the
 * chosen field, or "do not use" when its suggested field was taken away and
 * the header only contains a known name.
 */
export function learnedColumnNames(sheets: FixedPriceImportSheet[]): PriceListColumnName[] {
  const learned = new Map<string, PriceListColumnName>();
  for (const sheet of sheets) {
    if (!sheet.included || sheet.problem) continue;
    for (const column of sheet.columns) {
      const key = normaliseColumnName(column.header);
      if (!key || learned.has(key)) continue;
      const suggested = fieldOfColumn(sheet.detected, column.index);
      const chosen = fieldOfColumn(sheet.mapping, column.index);
      if (chosen === suggested) continue;
      if (chosen) learned.set(key, { name: column.header, field: chosen });
      else if (suggested && !column.match?.exact) learned.set(key, { name: column.header, field: 'ignore' });
    }
  }
  return [...learned.values()];
}

export const ROW_STATUS: Record<FixedPriceImportRowStatus, { label: string; cls: string; title: string }> = {
  matched: { label: 'Treffer', cls: 'pos', title: 'Wird übernommen.' },
  unmatched: {
    label: 'kein Treffer',
    cls: 'muted',
    title: 'Kein Artikel mit dieser Artikelnummer oder EAN. Wird nicht übernommen.',
  },
  ambiguous: {
    label: 'mehrdeutig',
    cls: 'warn',
    title: 'Artikelnummer und EAN passen zu verschiedenen Artikeln. Wird nicht übernommen.',
  },
  invalid: {
    label: 'kein Preis',
    cls: 'neg',
    title: 'Die Zeile hat keinen lesbaren Preis über 0. Wird nicht übernommen.',
  },
  duplicate: {
    label: 'doppelt',
    cls: 'muted',
    title: 'Für diesen Artikel steht weiter oben oder auf einem früheren Blatt schon ein Preis. Wird nicht übernommen.',
  },
};

/** A semicolon CSV that Excel opens with German umlauts intact. */
export function toCsv(header: string[], rows: (string | number | null)[][]): Blob {
  const quote = (value: string | number | null) => {
    const text = value == null ? '' : String(value);
    return /[;"\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };
  const lines = [header, ...rows].map((row) => row.map(quote).join(';'));
  return new Blob([`\uFEFF${lines.join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8' });
}
